/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, Observable, catchError, throwError, map, takeUntil, finalize } from 'rxjs';
import { HttpMethod } from '../../../enums/http-method';
import { ApiError } from '../../../interfaces/api-error.interface';
import { ApiRequestOptions } from '../../../interfaces/api-request-options.interface';
import { ApiRequestOptionsHttp } from '../../../interfaces/api-request-options-http.interface';
import { ApiServiceConfig } from '../../../interfaces/api-service-config.interface';

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	private _config: ApiServiceConfig;
	public pendingRequests = new Map<string, Subject<void>[]>();

	public get config(): ApiServiceConfig {
		return this._config;
	}

	public setConfig(config: ApiServiceConfig): void {
		this._config = config;
	}

	public request<T extends object>(options: ApiRequestOptions): Observable<T> {
		const { method, path, data, headers, params, shouldCancelPrevious, customUrl, isShimmer } =
			options;
		const url = customUrl ?? this.config.baseUrl;

		let correlationID = window.sessionStorage.getItem('correlationID');
		if (!correlationID) {
			correlationID = this.generateGuid();
			window.sessionStorage.setItem('correlationID', correlationID);
		}
		let header = headers ?? new HttpHeaders();
		header = header?.set('location', url);
		header = header?.set('x-itau-correlationID', correlationID);
		header = header?.set('Accept-Language', 'en-US');
		header = header?.set('Content-Type', 'application/json');
		header = header?.set('Authorization', correlationID)

		if (options.noLoading || isShimmer)
			header = header?.set('no-wait', isShimmer ? 'shimmer' : 'true');

		const optionsHttp: ApiRequestOptionsHttp = {
			headers: header,
			params: params,
			observe: 'response',
		};

		if (data) {
			if (method === HttpMethod.GET) {
				optionsHttp.params = this.translateObjectToHttpParams(data);
			} else if (method === HttpMethod.DELETE) {
				optionsHttp.params = this.translateObjectToHttpParams(data);
				optionsHttp.body = data;
			} else {
				optionsHttp.body = data;
			}
		}

		const fullUrl = url + path;
		// TODO: Adaptar proxy para funcionar nas chamadas AWS
		// if ((window.location.href.includes('localhost') || window.location.href.includes('127.0.0.1') ||
		// 	!!window.localStorage.getItem('FORCE_PROXY')) && !window.localStorage.getItem('DISABLE_PROXY'))
		// 	fullUrl = 'http://localhost:3000' + path
		if (shouldCancelPrevious && this.pendingRequests.has(fullUrl)) {
			this.cancelRequests(fullUrl);
		}

		const cancel$ = new Subject<void>();
		if (!this.pendingRequests.has(fullUrl)) {
			this.pendingRequests.set(fullUrl, []);
		}
		this.pendingRequests.get(fullUrl)?.push(cancel$);

		const request$ = this.config.http.request<T>(method, fullUrl, optionsHttp).pipe(
			catchError((error: HttpErrorResponse) =>
				throwError(() => {
					return <ApiError>{
						message: error.message,
						...error.error,
					};
				}),
			),
			map((response) => {
				return { ...response.body };
			}),
			takeUntil(cancel$),
			finalize(() => {
				this.removeCancelSubject(fullUrl, cancel$);
			}),
		);

		return request$ as Observable<T>;
	}

	public cancelRequests(url: string): void {
		const cancelSubjects = this.pendingRequests.get(url);
		if (cancelSubjects) {
			cancelSubjects.forEach((cancelSubject) => {
				cancelSubject.next();
			});
			this.pendingRequests.delete(url);
		}
	}

	private removeCancelSubject(url: string, cancelSubject: Subject<void>): void {
		const cancelSubjects = this.pendingRequests.get(url);
		if (cancelSubjects) {
			const index = cancelSubjects.indexOf(cancelSubject);
			if (index !== -1) {
				cancelSubjects.splice(index, 1);
			}
			if (cancelSubjects.length === 0) {
				this.pendingRequests.delete(url);
			}
		}
	}

	private translateObjectToHttpParams(object: any): HttpParams {
		let params = new HttpParams();
		Object.keys(object).forEach((key) => {
			params = params.append(key, object[key]);
		});
		return params;
	}

	private generateGuid(): string {
		return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c: any) =>
			(
				c ^
				(self?.crypto?.getRandomValues?.(new Uint8Array(1))[0] & (15 >> (c / 4)))
			)?.toString(16),
		);
	}
}
